/* eslint-disable max-lines */
import { ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';
import { FC, ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { colors } from '../../../config/colors';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const bounceUp = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
`;

const bounceDown = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(2px); }
`;

// Styled Components dla KpiCard
const StyledCard = styled.div`
  background: ${({ theme }) => theme.colors.chart.background};
  border-radius: 6px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const CardLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#111827')};
  font-weight: 300;
  line-height: 1.4;
  flex: 1;
`;

const CardIconWrapper = styled.div<{ $bgColor?: string }>`
  width: 42px;
  height: 42px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bgColor }) => $bgColor || 'rgba(59, 130, 246, 0.1)'};
  font-size: 20px;
  flex-shrink: 0;
`;

const CardValue = styled.div`
  font-size: 36px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#111827')};
  font-weight: 400;
  margin-bottom: 4px;
  line-height: 1.2;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-top: 4px;
`;

const SparklineContainer = styled.div`
  margin-top: 16px;
  height: 40px;
`;

// Styled Components dla KpiChange
const ChangeWrapper = styled.div<{ $variant: 'positive' | 'negative' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
  animation: ${fadeIn} 0.3s ease-out;
  flex-wrap: wrap;

  color: ${({ $variant, theme }) => {
    switch ($variant) {
      case 'positive':
        return theme.colors.success?.[500] || colors.success[500];
      case 'negative':
        return theme.colors.danger?.[500] || colors.danger[500];
      default:
        return theme.colors.neutral?.[600] || colors.neutral[600];
    }
  }};
`;

const ChangeIconWrapper = styled.span<{ $variant: 'positive' | 'negative' | 'neutral' }>`
  display: flex;
  align-items: center;
  font-size: 16px;
  
  ${({ $variant }) => {
    if ($variant === 'positive') {
      return css`
        animation: ${bounceUp} 0.6s ease-in-out;
      `;
    }
    if ($variant === 'negative') {
      return css`
        animation: ${bounceDown} 0.6s ease-in-out;
      `;
    }
    return '';
  }}
`;

const ChangeBadge = styled.span<{ $variant: 'positive' | 'negative' | 'neutral' }>`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 300;

  background: ${({ $variant }) => {
    switch ($variant) {
      case 'positive':
        return `${colors.success[500]}1A`;
      case 'negative':
        return `${colors.danger[500]}1A`;
      default:
        return `${colors.neutral[500]}1A`;
    }
  }};
`;

const ChangeTrendText = styled.span`
  font-size: 12px;
  opacity: 0.8;
`;

const ChangeValue = styled.span`
  font-weight: 300;
`;

// Interface dla KpiChange
interface KpiChangeProps {
    value?: number | string;
    type?: 'percentage' | 'absolute' | 'text';
    showIcon?: boolean;
    showBadge?: boolean;
    trendText?: string;
    previousValue?: number;
    className?: string;
}

// Komponent KpiChange
const KpiChange: FC<KpiChangeProps> = ({
  value,
  type = 'text',
  showIcon = true,
  showBadge = false,
  trendText,
  previousValue,
  className,
}) => {
  const getVariant = (): 'positive' | 'negative' | 'neutral' => {
    if (typeof value === 'number') {
      if (value > 0) return 'positive';
      if (value < 0) return 'negative';
      return 'neutral';
    }
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      if (lowerValue.includes('+') || lowerValue.includes('↑') || lowerValue.includes('wzrost')) {
        return 'positive';
      }
      if (lowerValue.includes('-') || lowerValue.includes('↓') || lowerValue.includes('spadek')) {
        return 'negative';
      }
    }
    return 'neutral';
  };

  const variant = getVariant();

  const getIcon = () => {
    switch (variant) {
      case 'positive':
        return <TrendingUp size={16} />;
      case 'negative':
        return <TrendingDown size={16} />;
      default:
        return <ArrowRight size={16} />;
    }
  };

  const formatValue = () => {
    if (typeof value === 'number') {
      switch (type) {
        case 'percentage':
          return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
        case 'absolute':
          return `${value > 0 ? '+' : ''}${value.toLocaleString()}`;
        default:
          return value.toString();
      }
    }
    return value;
  };

  const getTrendText = () => {
    if (trendText) return trendText;

    switch (variant) {
      case 'positive':
        return 'Wzrost';
      case 'negative':
        return 'Spadek';
      default:
        return 'Bez zmian';
    }
  };

  const renderPreviousComparison = () => {
    if (previousValue !== undefined && typeof value === 'number') {
      const diff = value - previousValue;
      const percentChange = previousValue !== 0
        ? ((diff / previousValue) * 100).toFixed(1)
        : '0.0';

      return (
        <ChangeTrendText>
          vs poprzedni okres:
          {' '}
          {diff > 0 ? '+' : ''}
          {percentChange}
          %
        </ChangeTrendText>
      );
    }
    return null;
  };

  if (value === undefined && value !== 0) return null;

  return (
    <ChangeWrapper $variant={variant} className={className}>
      {showIcon && (
        <ChangeIconWrapper $variant={variant}>
          {getIcon()}
        </ChangeIconWrapper>
      )}

      {showBadge ? (
        <ChangeBadge $variant={variant}>
          {formatValue()}
        </ChangeBadge>
      ) : (
        <ChangeValue>{formatValue()}</ChangeValue>
      )}
      {(trendText || variant !== 'neutral') && (
        <ChangeTrendText>{getTrendText()}</ChangeTrendText>
      )}

      {renderPreviousComparison()}
    </ChangeWrapper>
  );
};

// Interface dla KpiCard
interface KpiCardProps {
    label: string;
    value: string | number;
    change?: number | string;
    changeType?: 'percentage' | 'absolute' | 'text';
    subtitle?: string;
    icon?: ReactNode;
    iconBgColor?: string;
    showChangeIcon?: boolean;
    showChangeBadge?: boolean;
    trendText?: string;
    previousValue?: number;
    sparkline?: ReactNode;
    className?: string;
}

// Komponent KpiCard
export const KpiCard: FC<KpiCardProps> = ({
  label,
  value,
  change,
  changeType = 'text',
  subtitle,
  icon,
  iconBgColor,
  showChangeIcon = true,
  showChangeBadge = false,
  trendText,
  previousValue,
  sparkline,
  className,
}) => (
  <StyledCard className={className}>
    <CardHeader>
      <CardLabel>{label}</CardLabel>
      {icon && (
      <CardIconWrapper $bgColor={iconBgColor}>
        {icon}
      </CardIconWrapper>
      )}
    </CardHeader>

    <CardValue>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </CardValue>

    {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}

    {change !== undefined && (
    <KpiChange
      value={change}
      type={changeType}
      showIcon={showChangeIcon}
      showBadge={showChangeBadge}
      trendText={trendText}
      previousValue={previousValue}
    />
    )}

    {sparkline && (
    <SparklineContainer>
      {sparkline}
    </SparklineContainer>
    )}
  </StyledCard>
);

// Eksportuj też KpiChange osobno jeśli potrzebne
export { KpiChange };
